import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../../API";
import { RootState } from "../../app/store";
import { Role } from "../Auth/roles";
import { QueryStatus } from "../../app/types";
import type { AdminAuthority } from "../Auth/adminAccess";

interface UsersSlice {
  currentUser?: User;
  /**
   * Issuer-agnostic role list for the currently signed-in user. Populated
   * from `caller.roles` (returned by `/get-user` since Commit C) and/or
   * the legacy Cognito access-token groups during the rollout window —
   * see `features/Auth/roles.ts` for the mapping.
   */
  roles?: Role[];
  /**
   * Coaching / Rotations authority from the caller's JWT claims — the same
   * claims the RBAC Lambdas enforce with. Null until the session loads
   * (every Coaching / Rotations gate then reads closed).
   */
  adminAuthority: AdminAuthority | null;
  siblingUsers: User[];
  siblingUsersStatus: QueryStatus;
  siblingUsersError: string | null;
}

const initialState: UsersSlice = {
  currentUser: undefined,
  roles: [],
  adminAuthority: null,
  siblingUsers: [],
  siblingUsersStatus: "idle",
  siblingUsersError: null
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<User | undefined>) => {
      state.currentUser = action.payload;
    },
    setUserRoles: (state, action: PayloadAction<Role[]>) => {
      state.roles = action.payload;
    },
    setAdminAuthority: (state, action: PayloadAction<AdminAuthority | null>) => {
      state.adminAuthority = action.payload;
    },
    setSiblingUsersStatus: (state, action: PayloadAction<QueryStatus>) => {
      state.siblingUsersStatus = action.payload;
    },
    setSiblingUsers: (state, action: PayloadAction<User[]>) => {
      state.siblingUsers = action.payload;
    },
    setSiblingUsersError: (state, action: PayloadAction<string | null>) => {
      state.siblingUsersError = action.payload;
    }
  }
});

export const {
  setCurrentUser,
  setUserRoles,
  setAdminAuthority,
  setSiblingUsers,
  setSiblingUsersStatus,
  setSiblingUsersError
} = usersSlice.actions;

export const selectCurrentUser = (state: RootState) => state.users.currentUser;
export const selectUserRoles = (state: RootState) => state.users.roles;
export const selectAdminAuthority = (state: RootState) =>
  state.users.adminAuthority;
export const selectSiblingUsers = (state: RootState) =>
  state.users.siblingUsers;
export const selectSiblingUsersStatus = (state: RootState) =>
  state.users.siblingUsersStatus;
export const selectSiblingUsersError = (state: RootState) =>
  state.users.siblingUsersError;

export default usersSlice.reducer;
